import { useReducer, useMemo, useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function analyticsReducer(state, action) {
  if (action.type === 'view') {
    var view = state.hadFirstPageView && action.payload.unique ? // subsequent page views can never be unique, so we overwrite it
    _extends({}, action.payload, {
      unique: false
    }) : action.payload; // Avoid adding duplicate.
    // We should only need this in case a React Fast Refresh happened

    if (view.pathname === state.lastPathname) return state;
    return _extends({}, state, {
      queue: [].concat(state.queue, [view]),
      hadFirstPageView: true,
      lastPathname: view.pathname
    });
  }

  if (action.type === 'clear') {
    return _extends({}, state, {
      queue: state.queue.filter(function (view) {
        return !action.payload.includes(view);
      })
    });
  }

  return state;
} // the url we are passed looks like /projects?x=5
// but we are only interested in /projects


var omitQueryFromPathname = function omitQueryFromPathname(url) {
  var u = new URL("http://e.de" + url);
  return u.pathname;
};

function useAnalyticsServer() {}

function useAnalyticsClient(_ref) {
  var _ref$apiRoute = _ref.apiRoute,
      apiRoute = _ref$apiRoute === void 0 ? 'https://happykit.dev/api/pv' : _ref$apiRoute,
      publicKey = _ref.publicKey,
      skip = _ref.skip,
      _ref$skipHostnames = _ref.skipHostnames,
      skipHostnames = _ref$skipHostnames === void 0 ? ['localhost'] : _ref$skipHostnames,
      _ref$delay = _ref.delay,
      delay = _ref$delay === void 0 ? 5000 : _ref$delay;

  if (!publicKey) {
    throw new Error('@happykit/analytics: missing options.publicKey');
  }

  var router = useRouter();

  var _React$useReducer = useReducer(analyticsReducer, {
    queue: [],
    hadFirstPageView: false,
    lastPathname: ''
  }),
      state = _React$useReducer[0],
      send = _React$useReducer[1];

  var view = useMemo(function () {
    var referrer = document.referrer && new URL(document.referrer).hostname === window.location.hostname ? '' : document.referrer;
    var searchParams = new URLSearchParams(window.location.search);
    var urlReferrer = searchParams.get('ref') || undefined;
    return {
      hostname: window.location.hostname,
      // Actual path (excluding the query) shown in the browser
      pathname: omitQueryFromPathname(router.asPath),
      // The Next.js route. That is the path of the page in `/pages`.
      route: router.pathname,
      ua: navigator.userAgent,
      width: window.innerWidth,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      referrer: referrer,
      urlReferrer: urlReferrer,
      // This gets overwritten by the reducer, as subsequent page views can
      // never be unique
      unique: function () {
        if (document.referrer === '') return true;
        var ref = new URL(document.referrer);
        return ref.hostname !== window.location.hostname;
      }(),
      referrerHostname: urlReferrer ? urlReferrer : referrer ? new URL(referrer).hostname : undefined,
      referrerPathname: urlReferrer ? undefined : referrer ? new URL(referrer).pathname : undefined,
      time: Date.now()
    };
  }, [router.pathname, router.asPath]);
  var skipped = // skip ignored hostnames
  Array.isArray(skipHostnames) && skipHostnames.some(function (hostname) {
    return hostname.toLowerCase() === view.hostname.toLowerCase();
  }) || // skip events as defined by user
  typeof skip === 'function' && skip(view);
  useEffect(function () {
    if (skipped) return;
    send({
      type: 'view',
      payload: view
    });
  }, [view, skipped]);
  var queue = state.queue;
  var sendQueue = useCallback(function () {
    if (queue.length === 0) return;
    var views = [].concat(queue);
    send({
      type: 'clear',
      payload: views
    });

    if (typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(apiRoute, JSON.stringify({
        publicKey: publicKey,
        views: views
      }));
    } else {
      var body = JSON.stringify({
        publicKey: publicKey,
        views: views
      }); // Since sendBeacon sends a plain string, we don't set any
      // content-type headers on this request either.
      //
      // That way the server can parse the request body from a string.

      fetch(apiRoute, {
        method: 'POST',
        keepalive: true,
        body: body
      });
    }
  }, [queue, apiRoute, publicKey, send]);
  useEffect(function () {
    if (state.queue.length === 0) return;
    var supportsBeacon = typeof navigator.sendBeacon === 'function'; // sendBeacon works even when a browser window is being closed.
    // It's supported in most major browsers. We queue events in case
    // we can use sendBeacon, otherwise we send them live.

    var adjustedDelay = supportsBeacon ? delay : 0;

    if (!supportsBeacon) {
      sendQueue();
      return;
    }

    var timer = setTimeout(sendQueue, adjustedDelay);
    return function () {
      clearTimeout(timer);
    };
  }, [state, apiRoute, delay, sendQueue]); // send anything that hasn't been sent yet

  useEffect(function () {
    window.addEventListener('beforeunload', sendQueue, {
      once: true
    });
    return function () {
      window.removeEventListener('beforeunload', sendQueue);
    };
  }, [sendQueue]);
} // this runs on the client only


var useAnalytics = typeof window === 'undefined' ? useAnalyticsServer : useAnalyticsClient;

export { useAnalytics };
//# sourceMappingURL=analytics.esm.js.map
